import { z } from 'zod';
/**
 * The public API's error vocabulary (KIR-3602 design decision D8).
 *
 * A closed set on purpose. Internal handlers can name errors however they like;
 * a customer writing a SIEM connector needs a small, stable list they can branch
 * on, and one that never grows a new member inside a version. Adding a case here
 * is a breaking change for anyone who wrote an exhaustive switch.
 */
export var V1ErrorCode;
(function (V1ErrorCode) {
    V1ErrorCode["Unauthorized"] = "Unauthorized";
    V1ErrorCode["Forbidden"] = "Forbidden";
    V1ErrorCode["NotFound"] = "NotFound";
    V1ErrorCode["RateLimited"] = "RateLimited";
    V1ErrorCode["ValidationError"] = "ValidationError";
    /**
     * The request is well-formed and authorized, but the organization does not
     * have the feature it needs turned on (KIR-4431).
     *
     * Added before the first release, which is the only moment the closed set
     * above can gain a member for free. It replaces the earlier rule that a
     * disabled feature answered 404: a 404 kept flag state indistinguishable from
     * a missing row, but it also left an integrator with no way to tell "your org
     * cannot do this" from "you asked for something that is not there", which is
     * the difference between a support ticket and a config change they can make
     * themselves. The message names the feature.
     *
     * Not used for the `management_api_keys` flag itself, which stays a 403 —
     * that caller was handed a working key and needs a different answer.
     */
    V1ErrorCode["Conflict"] = "Conflict";
    /**
     * The caller sent an `expectedVersion` that no longer matches the stored
     * record, so the write was refused rather than applied over someone else's
     * change (KIR-4431, slice 3).
     *
     * **Why this is not `Conflict`.** The internal policy routes answer `409` for
     * both this and a disabled feature, which is fine for the app — it knows
     * which call it made. A public caller is told to branch on `error` and never
     * on `message`, so collapsing the two would leave them unable to tell "retry
     * with a fresh version" from "your organization does not have Enforce mode".
     * The first is a loop the client should run; the second is a config change
     * only a human can make. A client that cannot separate them cannot implement
     * either correctly.
     *
     * `412 Precondition Failed` is the HTTP semantic for exactly this: the
     * caller stated a precondition and it did not hold. The internal routes keep
     * answering `409` — this is a route-level adaptation for a different
     * audience, like the `409 → 200` idempotency case on the alert suppression
     * writes.
     *
     * Added before the first release, the only moment this closed set can gain a
     * member for free — the same argument that admitted `Conflict`.
     */
    V1ErrorCode["PreconditionFailed"] = "PreconditionFailed";
    /**
     * The write would create something that already exists — today, a policy
     * exception matching one already on the policy (KIR-4431, slice 3).
     *
     * **Why this is not `Conflict`.** Both answer `409`, and for a while this case
     * did collapse into `Conflict` — which reserves its meaning for "your
     * organization does not have this feature". Those need different reactions and
     * a client branches on the code, not the status: a duplicate is something the
     * caller can resolve by reading the existing exception and adjusting their
     * request, whereas a disabled feature is a flag that will never clear on its
     * own. Sharing one code left them indistinguishable, and the generic message
     * for `Conflict` would have talked about feature enablement. Found by Bugbot
     * on #2487.
     *
     * Added before the first release, the only moment this closed set can gain a
     * member for free — the same argument that admitted `Conflict` and
     * `PreconditionFailed`.
     */
    V1ErrorCode["AlreadyExists"] = "AlreadyExists";
    V1ErrorCode["Internal"] = "Internal";
})(V1ErrorCode || (V1ErrorCode = {}));
/**
 * Every non-2xx `/api/v1` response body.
 *
 * `requestId` is the trace id Cloud Trace and every log line for the request
 * carry, so a customer can quote it in a support ticket and we can find the
 * exact request. It is the reason this envelope differs from the internal
 * `errorResponseSchema` rather than reusing it.
 */
export const v1ErrorResponseSchema = z
    .object({
    error: z
        .nativeEnum(V1ErrorCode)
        .describe('Machine-readable error code from a closed set. Branch on this, never on `message`.'),
    message: z
        .string()
        .describe('Human-readable explanation. On 4xx it is written for you and names what to change; on 5xx it is a fixed generic string, because the underlying error can name internal details.'),
    requestId: z
        .string()
        .describe('Trace ID for this request, also carried by every log line it produced. Quote it to Knostic support to have the exact request looked up.'),
})
    .meta({
    id: 'V1Error',
    title: 'Error',
    description: 'The body of every non-2xx response from this API. Adding a new `error` code is a breaking change, so an exhaustive switch stays exhaustive for the life of v1.',
    examples: [
        {
            error: 'Forbidden',
            message: 'This API key is not scoped to the requested organization',
            requestId: '4f7a1c9e2b6d8035a1e4c7f0b93d5628',
        },
    ],
});
//# sourceMappingURL=errors.js.map